import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, Card, Dialog } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  InvoiceClient,
  invoiceGrandTotal,
  invoiceLineCommission,
  invoiceLineNet,
  invoiceLineTax,
  invoiceOrderTypeLabel,
  invoiceTaxTotal,
  type InvoiceDTO,
} from "../api/invoice";

const TYPE_VARIANT = { ORDER: "accent", OTHER: "neutral", REFUND: "error", "": "neutral" } as const;

/**
 * What SIGM's `inv_status` means, for the three letters we actually know.
 *
 * `F` is *facturé* and the only status that carries an invoice number — every
 * other row in the table has none, which is what makes "invoiced" a checkable
 * property rather than a reading of the letter. `E` is the wholesale programme:
 * shipped or waiting to ship, not yet billed. `A` is void, and the reporting
 * queries have always excluded it.
 *
 * `C` and `M` are left as their letter on purpose. Nobody has confirmed what
 * they mean, and a guessed label on a filter people will trust is worse than
 * an unexplained one.
 */
const STATUS = {
  F: { key: "invoice.status.invoiced", label: "Invoiced", variant: "accent" },
  E: { key: "invoice.status.pending", label: "Not yet invoiced", variant: "warning" },
  A: { key: "invoice.status.void", label: "Void", variant: "neutral" },
} as const;

/**
 * The invoice master grid, shared by the Sales → Invoices page and the product
 * editor's Invoices tab. Row click opens the full detail — general, amounts,
 * addresses and lines — fetched on demand, since headers are what the period
 * query returns.
 */
function statusLabel(status: string | null | undefined, t: (key: string, fallback: string) => string): string {
  const trimmed = (status ?? "").trim();
  const known = STATUS[trimmed as keyof typeof STATUS];
  return known ? t(known.key, known.label) : trimmed || "";
}

export function InvoicesTable({
  data,
  loading,
  toolbarExtra,
  awaiting = false,
}: {
  data: InvoiceDTO[];
  loading: boolean;
  /** Page-level controls, right of the search — the period pickers. */
  toolbarExtra?: ReactNode;
  /**
   * The rows are confirmed slips still waiting for an invoice number. The
   * state and the days waiting lead, the slip number replaces the invoice
   * number (always 0 here), the warehouse shows, and the status letter — the
   * same C on every row — is dropped.
   */
  awaiting?: boolean;
}) {
  const { t } = useTranslation();
  const [detailId, setDetailId] = useState<number | null>(null);

  const cols = useMemo(() => {
    const c = defineColumns<InvoiceDTO>();
    const regular = [
      c.text("no", (row) => row.no ?? "", { headerKey: "invoice.no", priority: 1 }),
      c.text("date", (row) => row.date ?? "", {
        headerKey: "invoice.date",
        filter: "daterange",
        priority: 2,
      }),
      c.custom(
        "type",
        (row) => invoiceOrderTypeLabel(row.order_type),
        (row) => {
          const label = invoiceOrderTypeLabel(row.order_type);
          return (
            <Badge variant={TYPE_VARIANT[label]} size="sm">
              {label}
            </Badge>
          );
        },
        { headerKey: "invoice.type", filter: "select", priority: 3 },
      ),
      c.custom(
        "status",
        (row) => statusLabel(row.status, t),
        (row) => {
          const known = STATUS[(row.status ?? "") as keyof typeof STATUS];
          return (
            <Badge variant={known ? known.variant : "neutral"} size="sm">
              {statusLabel(row.status, t)}
            </Badge>
          );
        },
        { headerKey: "column.status", filter: "select", priority: 3 },
      ),
      c.text("currency", (row) => row.currency ?? "", {
        headerKey: "column.currency",
        filter: "select",
        priority: 3,
      }),
      c.text("warehouse", (row) => row.warehouse ?? "", {
        headerKey: "invoice.warehouse",
        filter: "select",
        defaultVisible: false,
      }),
      c.text("client", (row) => row.client?.cli_name1 ?? "", {
        headerKey: "column.client",
        priority: 2,
      }),
      c.text("order_no", (row) => row.order_no ?? "", {
        headerKey: "invoice.order-no",
        defaultVisible: false,
      }),
      c.text("client_order_no", (row) => row.client_order_no ?? "", {
        headerKey: "invoice.client-order-no",
        defaultVisible: false,
      }),
      c.text("salesperson", (row) => row.salesperson_name ?? "", {
        headerKey: "invoice.salesperson",
        defaultVisible: false,
      }),
      c.text("carrier", (row) => row.carrier ?? "", {
        headerKey: "invoice.carrier",
        defaultVisible: false,
      }),
      c.number("subtotal", (row) => round(row.subtotal), { headerKey: "invoice.subtotal" }),
      c.number("shipping", (row) => round(row.shipping_amount), {
        headerKey: "invoice.shipping",
        defaultVisible: false,
      }),
      c.number("discount", (row) => round(row.discount_amount), {
        headerKey: "invoice.discount",
        defaultVisible: false,
      }),
      c.number("tax", (row) => round(invoiceTaxTotal(row)), {
        headerKey: "invoice.tax",
        defaultVisible: false,
      }),
      c.number("total", (row) => round(invoiceGrandTotal(row)), {
        headerKey: "invoice.total",
        priority: 3,
      }),
      c.text("ship_to", (row) => [row.ship_name1, row.ship_city].filter(Boolean).join(", "), {
        headerKey: "invoice.ship-to",
        defaultVisible: false,
      }),
    ];
    if (!awaiting) return regular;

    const lead = [
      c.custom(
        "pending_state",
        (row) => row.pending_state ?? "",
        (row) =>
          row.pending_state === "STUCK" ? (
            <Badge variant="error" size="sm">
              {t("invoice.awaiting.stuck", "Stuck")}
            </Badge>
          ) : (
            <Badge variant="warning" size="sm">
              {t("invoice.awaiting.to-invoice", "To invoice")}
            </Badge>
          ),
        {
          headerKey: "invoice.awaiting.state",
          filter: "select",
          filterOptions: [
            { value: "STUCK", label: t("invoice.awaiting.stuck", "Stuck") },
            { value: "TO_INVOICE", label: t("invoice.awaiting.to-invoice", "To invoice") },
          ],
          priority: 1,
        },
      ),
      c.number("days_waiting", (row) => row.days_waiting, { headerKey: "invoice.awaiting.days", priority: 2 }),
      c.text("packing_slip_no", (row) => String(row.packing_slip_no ?? ""), {
        headerKey: "invoice.packing-slip",
        priority: 1,
      }),
    ];
    const shown = new Set(["warehouse", "order_no"]);
    const confirmed = c.text("date", (row) => row.date ?? "", {
      headerKey: "invoice.awaiting.confirmed",
      filter: "daterange",
      priority: 2,
    });
    return [
      ...lead,
      ...regular
        .filter((column) => column.id !== "no" && column.id !== "status")
        .map((column) => {
          if (column.id === "date") return confirmed;
          if (shown.has(column.id ?? "")) {
            return { ...column, meta: { ...(column.meta ?? {}), defaultVisible: true } };
          }
          return column;
        }),
    ];
  }, [awaiting, t]);

  return (
    <>
      <DataTable
        // Each mode keeps its own column defaults; remounting on the switch is
        // what applies them, since visibility is otherwise held from mount.
        key={awaiting ? "awaiting" : "invoiced"}
        {...(toolbarExtra ? { toolbarExtra } : {})}
        data={data}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={loading}
        onRowClick={(row) => setDetailId(row.id)}
        emptyMessage={
          awaiting
            ? t("invoice.awaiting.empty", "Every confirmed slip has its invoice.")
            : t("table.empty", "Nothing to show")
        }
      />
      {detailId !== null && <InvoiceDetailDialog id={detailId} onClose={() => setDetailId(null)} />}
    </>
  );
}

function InvoiceDetailDialog({ id, onClose }: { id: number; onClose: () => void }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new InvoiceClient(http), [http]);

  const { data, isLoading } = useQuery({
    queryKey: ["invoice", "detail", id],
    queryFn: () => client.detail(id),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("invoice.detail.title", "Invoice {{no}}", { no: data?.no ?? "…" })}
      description={data?.client?.cli_name1 ?? ""}
      size="lg"
      actions={
        <Button variant="secondary" onClick={onClose}>
          {t("button.close", "Close")}
        </Button>
      }
    >
      {isLoading || !data ? (
        <div className="flex h-40 items-center justify-center">
          <span className="size-5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Section title={t("invoice.section.general", "General")}>
              <Row label={t("invoice.date", "Date")} value={data.date} />
              <Row label={t("invoice.type", "Type")} value={invoiceOrderTypeLabel(data.order_type)} />
              <Row label={t("column.status", "Status")} value={data.status} />
              <Row label={t("invoice.warehouse", "Warehouse")} value={data.warehouse} />
              <Row label={t("invoice.order-no", "Order #")} value={data.order_no} />
              <Row label={t("invoice.salesperson", "Salesperson")} value={data.salesperson_name} />
              <Row label={t("invoice.carrier", "Carrier")} value={data.carrier} />
              <Row label={t("invoice.client-order-no", "Client order #")} value={data.client_order_no} />
              <Row label={t("invoice.packing-slip", "Packing slip")} value={data.packing_slip_no} />
              <Row label={t("column.tax-group", "Tax group")} value={data.client_tax_group_no} />
            </Section>
            <Section title={t("invoice.section.amounts", "Amounts")}>
              <Row label={t("invoice.subtotal", "Subtotal")} value={money(data.subtotal, data.currency)} />
              <Row label={t("invoice.shipping", "Shipping")} value={money(data.shipping_amount, data.currency)} />
              <Row label={t("invoice.discount", "Discount")} value={money(data.discount_amount, data.currency)} />
              <Row label={t("invoice.tax", "Taxes")} value={money((data.tax1_amount ?? 0) + (data.tax2_amount ?? 0), data.currency)} />
              <Row label={t("invoice.total", "Total")} value={money(invoiceGrandTotal(data), data.currency)} strong />
            </Section>
            <Section title={t("invoice.section.bill-to", "Bill to")}>
              <Row label={t("field.name", "Name")} value={data.bill_name1} />
              <Row label={t("field.address", "Address")} value={data.bill_address} />
              {data.bill_name2 && <Row label={t("field.address-2", "Address line 2")} value={data.bill_name2} />}
              <Row label={t("field.city", "City")} value={data.bill_city} />
              <Row label={t("field.postal-code", "Postal code")} value={data.bill_postal_code} />
              <Row label={t("field.phone", "Phone")} value={data.bill_phone} />
            </Section>
            <Section title={t("invoice.section.ship-to", "Ship to")}>
              <Row label={t("field.name", "Name")} value={data.ship_name1} />
              <Row label={t("field.address", "Address")} value={data.ship_address} />
              {data.ship_name2 && <Row label={t("field.address-2", "Address line 2")} value={data.ship_name2} />}
              <Row label={t("field.city", "City")} value={data.ship_city} />
              <Row label={t("field.postal-code", "Postal code")} value={data.ship_postal_code} />
              <Row label={t("field.phone", "Phone")} value={data.ship_phone} />
              <Row label={t("field.email", "Email")} value={data.ship_email} />
            </Section>
          </div>

          {(data.note1 || data.note2 || data.note3 || data.note4) && (
            <Section title={t("invoice.section.notes", "Notes")}>
              {[data.note1, data.note2, data.note3, data.note4]
                .filter(Boolean)
                .map((note, index) => (
                  <p key={index} className="text-[13px] text-ink-muted">
                    {note}
                  </p>
                ))}
            </Section>
          )}

          <Section title={t("invoice.section.lines", "Lines")}>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-line text-[11px] uppercase tracking-wider text-ink-faint">
                    <th className="py-1.5 pr-3 text-left">{t("column.line", "Line")}</th>
                    <th className="py-1.5 pr-3 text-left">{t("column.sku", "SKU")}</th>
                    <th className="py-1.5 pr-3 text-left">{t("column.name", "Description")}</th>
                    <th className="py-1.5 pr-3 text-left">{t("column.status", "Status")}</th>
                    <th className="py-1.5 pr-3 text-right">{t("column.qty.ordered", "Ordered")}</th>
                    <th className="py-1.5 pr-3 text-right">{t("invoice.line.qty", "Shipped")}</th>
                    <th className="py-1.5 pr-3 text-right">{t("invoice.line.price", "Price")}</th>
                    <th className="py-1.5 pr-3 text-right">{t("invoice.line.discount", "Disc. %")}</th>
                    <th className="py-1.5 pr-3 text-right">{t("column.total-cost", "Cost")}</th>
                    <th className="py-1.5 pr-3 text-right">{t("column.commission", "Commission")}</th>
                    <th className="py-1.5 pr-3 text-right">{t("column.tax-1", "Tax 1")}</th>
                    <th className="py-1.5 pr-3 text-right">{t("column.tax-2", "Tax 2")}</th>
                    <th className="py-1.5 pr-3 text-right">{t("column.line-total", "Line total")}</th>
                    <th className="py-1.5 text-left">{t("column.ship-date", "Ship date")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.lines ?? []).map((line) => (
                    <tr key={line.id} className="border-b border-line-soft last:border-0">
                      <td className="py-1.5 pr-3 tabular">{line.seq_no ?? ""}</td>
                      <td className="py-1.5 pr-3 font-medium text-ink">{line.part?.no ?? ""}</td>
                      <td className="py-1.5 pr-3 text-ink-muted">{line.part?.description1 ?? ""}</td>
                      <td className="py-1.5 pr-3 text-ink-muted">{line.status ?? ""}</td>
                      <td className="tabular py-1.5 pr-3 text-right">{line.ordered_qty ?? 0}</td>
                      <td className="tabular py-1.5 pr-3 text-right">{line.ship_qty ?? 0}</td>
                      <td className="tabular py-1.5 pr-3 text-right">{money(line.price, data.currency)}</td>
                      <td className="tabular py-1.5 pr-3 text-right">{`${line.discount_percent ?? 0}%`}</td>
                      <td className="tabular py-1.5 pr-3 text-right">{money(line.cost, data.currency)}</td>
                      <td className="tabular py-1.5 pr-3 text-right">
                        {money(invoiceLineCommission(line), data.currency)}
                        <span className="ml-1 text-[11px] text-ink-faint">{`${line.commission_percent ?? 0}%`}</span>
                      </td>
                      <td className="tabular py-1.5 pr-3 text-right">
                        {money(invoiceLineTax(data, line, 1), data.currency)}
                      </td>
                      <td className="tabular py-1.5 pr-3 text-right">
                        {money(invoiceLineTax(data, line, 2), data.currency)}
                      </td>
                      <td className="tabular py-1.5 pr-3 text-right font-medium">
                        {money(invoiceLineNet(line), data.currency)}
                      </td>
                      <td className="py-1.5 text-ink-muted">{line.ship_date ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </div>
      )}
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card className="p-3">
      <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-faint">{title}</h4>
      {children}
    </Card>
  );
}

function Row({ label, value, strong }: { label: string; value: string | null | undefined; strong?: boolean }) {
  return (
    <p className="flex items-baseline justify-between gap-3 py-0.5 text-[13px]">
      {label && <span className="text-ink-muted">{label}</span>}
      <span className={strong ? "tabular font-semibold text-ink" : "tabular text-ink"}>{value || ""}</span>
    </p>
  );
}

function money(value: number | null | undefined, currency: string | null | undefined): string {
  if (value === null || value === undefined) return "";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currency ?? "CAD" }).format(value);
  } catch {
    return value.toFixed(2);
  }
}

function round(value: number | null | undefined): number | null {
  return value === null || value === undefined ? null : Math.round(value * 100) / 100;
}
